import { useFormField } from '@calendly/form';
import { useBooleanState } from '@calendly/hooks';
import { I18n } from '@calendly/i18n';
import { IconButton } from '@calendly/ui/components/icon-button';
import { ArrowRecurringIcon, EditPencilIcon, InterfaceLinkBrokenIcon } from '@calendly/ui/components/icons';
import { BodyText } from '@calendly/ui/components/typography';
import { colorTextSecondary } from '@calendly/ui/theme';
import classNames from 'classnames';
import { useCallback, forwardRef } from 'react';
import { useGetUserQuery } from 'common';
import { getMemberName } from 'components/event_type_edit';
import { getAvailabilityFromRule } from 'components/event_type_edit/utils/get_availability_from_rule';
import { Layout, LayoutItem } from 'components/layout';
import { useEventType } from 'features/event_type_provider';
import { useTrackAvailabilityInteraction } from 'hooks/analytics_events/use_track_availability_interaction';
import { ReadOnlyHoursAndDateOverrides } from './components';
import { DefaultAvailabilityModal } from './components/default_availability_modal';
import { HeaderWithIcon, PanelContentHeader } from '../../styles';
const delimiterCss = "d1ekopdq";
const Delimiter = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(delimiterCss, props.className)} />;
});
const customContextualBannerCss = "c1v0bmyx";
const CustomContextualBanner = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(customContextualBannerCss, props.className)} />;
});
const Content = ({
  children
}) => <Layout paddingX="24" paddingY="16" gap="8" direction="column">
    <PanelContentHeader>
      <HeaderWithIcon>
        <ArrowRecurringIcon color={colorTextSecondary} />
        <BodyText size="medium">
          {I18n.t('publisher.event_type_editor.sections.available_hours.weekly_hours')}
        </BodyText>
      </HeaderWithIcon>
    </PanelContentHeader>
    {children}
  </Layout>;
const ControlsLayout = ({
  children
}) => <Layout paddingX="24" paddingY="16" gap="8" justify="space-between" align="center">
    <BodyText size="medium" color="muted">
      {I18n.t('publisher.event_type_editor.sections.availability.schedule.actions.schedule_description')}
    </BodyText>
    <LayoutItem relative>
      {children}
    </LayoutItem>
  </Layout>;
export const ScheduleAvailableHours = ({
  formPath
}) => {
  const {
    data: currentUser
  } = useGetUserQuery();
  const [{
    value
  }, _, {
    setValue
  }] = useFormField({
    name: formPath
  });
  const {
    activeAvailabilityRuleId,
    usingCustomHours,
    name,
    userUuid,
    ownerAvailabilityRules
  } = value;
  const memberName = getMemberName({
    name,
    user_uuid: userUuid
  }, currentUser);
  const [isAvailabilityModalOpen, openAvailabilityModal, closeAvailabilityModal] = useBooleanState(false);
  const eventType = useEventType();
  const primaryRuleId = ownerAvailabilityRules?.primaryRuleId || ownerAvailabilityRules?.primary_rule_id;
  const memberSchedules = ownerAvailabilityRules?.rules;
  const activeSchedule = !usingCustomHours ?? memberSchedules ? memberSchedules.find(({
    id
  }) => id === activeAvailabilityRuleId) : null;
  const primaryAvailability = getAvailabilityFromRule(activeSchedule);
  const isCurrentUserAvailability = currentUser.uuid === userUuid;
  const trackETEAvailabilityInteraction = useTrackAvailabilityInteraction({
    eventType,
    location: 'et_editor'
  });
  const handleEditSchedule = event => {
    if (primaryRuleId === activeAvailabilityRuleId) trackETEAvailabilityInteraction({
      action: 'edit_your_default_schedule'
    });
    return openAvailabilityModal(event);
  };
  const handleEditButtonClick = event => handleEditSchedule(event);
  const handleDisconnect = useCallback(() => {
    trackETEAvailabilityInteraction({
      action: 'customize_schedule_for_this_event_type_only'
    });
    setValue({
      ...value,
      usingCustomHours: true,
      usingSecondarySchedule: false,
      ...primaryAvailability
    });
  }, [primaryAvailability, setValue, trackETEAvailabilityInteraction, value]);
  const handleClose = useCallback(({
    updatedSchedule
  } = {}) => {
    if (updatedSchedule) {
      setValue({
        ...value,
        ownerAvailabilityRules: {
          ...ownerAvailabilityRules,
          rules: ownerAvailabilityRules.rules.map(rule => rule.id === updatedSchedule.id ? {
            ...rule,
            rules: updatedSchedule.rules,
            timezone: updatedSchedule.timezone
          } : rule)
        }
      });
    }
    closeAvailabilityModal();
  }, [closeAvailabilityModal, ownerAvailabilityRules, setValue, value]);
  if (!activeSchedule) {
    return null;
  }
  return <CustomContextualBanner>
      <Layout direction="column">
        <ControlsLayout isCurrentUserAvailability={isCurrentUserAvailability} memberName={memberName}>
          {isCurrentUserAvailability ? <IconButton size="small" title={I18n.t('publisher.event_type_editor.sections.availability.schedule.actions.edit_schedule')} onClick={handleEditButtonClick}>
              <EditPencilIcon />
            </IconButton> : <IconButton size="small" title={I18n.t('publisher.event_type_editor.sections.availability.schedule.actions.disconnect_default')} onClick={handleDisconnect}>
              <InterfaceLinkBrokenIcon />
            </IconButton>}
        </ControlsLayout>
        {isAvailabilityModalOpen && <DefaultAvailabilityModal activeAvailabilityRuleId={activeAvailabilityRuleId} onClose={handleClose} />}
        <Delimiter />
        <Content>
          <ReadOnlyHoursAndDateOverrides availableHours={primaryAvailability.availableHours} timezone={primaryAvailability.timezone} dateOverrides={primaryAvailability.dateOverrides} />
        </Content>
      </Layout>
    </CustomContextualBanner>;
};

require("./schedule_available_hours.wyw-in-js.css!=!../../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./schedule_available_hours.js");